import React, { useRef, useState } from "react";
import { forgotPasswordApi } from "utils/authApi";
import { useNavigate } from "react-router-dom";
import { AuthFormField } from "components/auth-form/AuthForm";
import { parseApiError } from "utils/errors/parseApiError";

const EMAIL_RE = /\S+@\S+\.\S+/;

/**
 * Shown on success AND when Firebase reports no such account.
 *
 * Never confirm whether an address has an account. The backend's sign-in path goes out of its way
 * to make "no such account" and "wrong password" byte-identical for the same reason, and a
 * forgot-password form that answers differently is a cleaner oracle than the login form ever was.
 */
const NEUTRAL_CONFIRMATION =
  "If there's an account for that address, we've sent it a temporary password. Sign in with " +
  "it and you'll be asked to choose a new one. Check your inbox, and your spam folder.";

export interface UseForgotPasswordReturn {
  fields: AuthFormField[];
  submitRef: React.RefObject<HTMLButtonElement>;
  isSubmitDisabled: boolean;
  showLoading: boolean;
  loadingMsg: string;
  showError: boolean;
  errorMsg: string;
  showSuccess: boolean;
  successMsg: string;
  handleSubmit: () => Promise<void>;
  handleCloseError: () => void;
  handleCloseSuccess: () => void;
  handleBackToLogin: () => void;
}

export function useForgotPassword(): UseForgotPasswordReturn {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [showLoading, setShowLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState("");
  const [showError, setShowError] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  const submitRef = useRef<HTMLButtonElement>(null);

  const openError = (msg: string) => {
    setLoadingMsg("");
    setShowLoading(false);
    setErrorMsg(msg);
    setShowError(true);
  };

  const handleCloseError = () => {
    setShowError(false);
    setErrorMsg("");
  };

  const handleBackToLogin = () => navigate("/", { replace: true });

  const handleCloseSuccess = () => {
    setShowSuccess(false);
    setSuccessMsg("");
    handleBackToLogin();
  };

  const handleSubmit = async () => {
    const address = email.trim();
    if (!EMAIL_RE.test(address)) {
      return openError("Please enter a valid email address.");
    }

    try {
      setLoadingMsg("Sending temporary password…");
      setShowLoading(true);

      await forgotPasswordApi(address);

      setShowLoading(false);
      setLoadingMsg("");
      setSuccessMsg(NEUTRAL_CONFIRMATION);
      setShowSuccess(true);
    } catch (err: any) {
      const { code, message } = parseApiError(
        err,
        "We couldn't send the temporary password. Please try again.",
      );

      // Not an error as far as the user is told — see NEUTRAL_CONFIRMATION. This branch only runs
      // when the project has Email Enumeration Protection off; with it on, Firebase resolves
      // successfully for an unknown address and we never get here.
      if (code === "auth/user-not-found") {
        setShowLoading(false);
        setLoadingMsg("");
        setSuccessMsg(NEUTRAL_CONFIRMATION);
        setShowSuccess(true);
        return;
      }

      openError(message);
    }
  };

  const submitOnEnter = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") submitRef.current?.click();
  };

  const fields: AuthFormField[] = [
    {
      name: "email",
      label: "Email",
      type: "email",
      value: email,
      onChange: setEmail,
      onKeyDown: submitOnEnter,
      placeholder: "Enter your email",
      autoComplete: "email",
    },
  ];

  return {
    fields,
    submitRef,
    isSubmitDisabled: !email.trim(),
    showLoading,
    loadingMsg,
    showError,
    errorMsg,
    showSuccess,
    successMsg,
    handleSubmit,
    handleCloseError,
    handleCloseSuccess,
    handleBackToLogin,
  };
}
